import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button, Card, PageState, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { useAdminAutomationFlows, useDeleteAutomationFlow } from '../../api/automation-flows';

/** Platform-admin CRUD for the admin-authored conversation flow library (docs/API_SPEC.md section 22). */
export function AutomationFlowsAdminPage() {
  const { isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const flows = useAdminAutomationFlows();
  const del = useDeleteAutomationFlow();

  if (!isPlatformAdmin) return <Navigate to="/" replace />;

  async function remove(id: string, name: string) {
    if (!window.confirm(`Delete flow "${name}"? This cannot be undone.`)) return;
    try {
      await del.mutateAsync(id);
      toast.success('Flow deleted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the flow.');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Conversation flows</h1>
        <span style={{ flex: 1 }} />
        <Button onClick={() => navigate('/admin/automation-flows/new')}>New flow</Button>
      </div>
      <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
        Multi-step WhatsApp conversations (wait for a reply, branch, list live data, take actions) a{' '}
        <code>START_FLOW</code> automation action can start. Reference one by its <code>key</code>{' '}
        from an automation, or bundle it into an{' '}
        <Link to="/admin/automation-templates">automation template</Link>.
      </p>

      <Card title="Library">
        {flows.isPending ? (
          <PageState kind="loading" message="Loading flows…" />
        ) : flows.isError ? (
          <PageState
            kind="error"
            message={flows.error.message}
            onRetry={() => void flows.refetch()}
          />
        ) : flows.data.items.length === 0 ? (
          <PageState kind="empty" title="No flows yet" message="Create one above." />
        ) : (
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 'var(--space-2)',
            }}
          >
            {flows.data.items.map((f) => (
              <li
                key={f.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  borderBottom: '1px solid var(--color-border)',
                  paddingBottom: 'var(--space-2)',
                }}
              >
                <strong>{f.name}</strong>
                <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                  {f.key}
                </span>
                <span className="ne-badge">{f.stepCount} steps</span>
                <span className="ne-badge">{f.status}</span>
                {f.isSystem ? <span className="ne-badge">system</span> : null}
                <span style={{ flex: 1, minWidth: 0 }} />
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/admin/automation-flows/${f.id}/edit`)}
                >
                  View / Edit
                </Button>
                <Button
                  variant="secondary"
                  disabled={del.isPending}
                  onClick={() => void remove(f.id, f.name)}
                >
                  Delete
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/admin/automation-templates">Automation templates</Link> ·{' '}
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
